import { create } from 'zustand';
import type { Project } from '@/storage/ProjectRepository';
import { projectStore } from '@/storage/projectStorePort';
import { createDefaultAppDefinition } from '@/utils/templates';
import type { AppDefinition } from '@/types/app';
import { useAuthStore } from '@/store/authStore';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadProjects: () => Promise<void>;
  createProject: (name: string, description?: string) => Promise<Project>;
  createFromDefinition: (name: string, description: string | undefined, definition: AppDefinition) => Promise<Project>;
  updateProject: (
    id: string,
    updates: Partial<Pick<Project, 'name' | 'description' | 'definition' | 'thumbnail' | 'is_published'>>
  ) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  duplicateProject: (id: string) => Promise<Project>;
  setCurrentProject: (project: Project | null) => void;
  loadProject: (id: string) => Promise<void>;
}

export const useProjectStore = create<ProjectState>((set) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,

  loadProjects: async () => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) {
      set({ projects: [], isLoading: false, error: null });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await projectStore().claimUnowned(ownerId);
      const projects = await projectStore().getAll(ownerId);
      set({ projects, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to load projects' });
    }
  },

  createProject: async (name: string, description?: string) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) throw new Error('No authenticated user');
    set({ isLoading: true, error: null });
    try {
      const definition = createDefaultAppDefinition(name);
      const project = await projectStore().create({ name, description, definition }, ownerId);
      const projects = await projectStore().getAll(ownerId);
      set({ projects, isLoading: false });
      return project;
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to create project' });
      throw e;
    }
  },

  createFromDefinition: async (name, description, definition) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) throw new Error('No authenticated user');
    set({ isLoading: true, error: null });
    try {
      const project = await projectStore().create({ name, description, definition }, ownerId);
      set((state) => ({
        projects: [project, ...state.projects],
        currentProject: project,
        isLoading: false,
      }));
      return project;
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to create project' });
      throw e;
    }
  },

  updateProject: async (id, updates) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) throw new Error('No authenticated user');
    set({ isLoading: true, error: null });
    try {
      await projectStore().update(id, updates, ownerId);
      const projects = await projectStore().getAll(ownerId);
      set((state) => ({
        projects,
        isLoading: false,
        currentProject:
          state.currentProject?.id === id
            ? { ...state.currentProject, ...updates }
            : state.currentProject,
      }));
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to update project' });
      throw e;
    }
  },

  deleteProject: async (id) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) throw new Error('No authenticated user');
    set({ isLoading: true, error: null });
    try {
      await projectStore().delete(id, ownerId);
      const projects = await projectStore().getAll(ownerId);
      set((state) => ({
        projects,
        isLoading: false,
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
      }));
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to delete project' });
      throw e;
    }
  },

  duplicateProject: async (id) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) throw new Error('No authenticated user');
    set({ isLoading: true, error: null });
    try {
      const project = await projectStore().duplicate(id, ownerId);
      const projects = await projectStore().getAll(ownerId);
      set({ projects, isLoading: false });
      return project;
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to duplicate project' });
      throw e;
    }
  },

  setCurrentProject: (project) => {
    set({ currentProject: project });
  },

  loadProject: async (id) => {
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) {
      set({ currentProject: null, isLoading: false });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const project = await projectStore().getById(id, ownerId);
      set({ currentProject: project, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Failed to load project' });
      throw e;
    }
  },
}));
